import React, { useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import { injectIntl } from 'react-intl';
import type { InjectedIntl } from 'react-intl';
import {
  MDRT_COMMISSION,
  NEWCOMER_DEFAULTS,
  SIM_YEARS,
  TEAM_DEFAULTS,
  differenceOf,
  formatMoney,
  formatPeople,
  formatWan,
  niceMax,
  simulateNewcomer,
  simulateTeam
} from './incomeModel';
import type { NewcomerInputs, TeamInputs } from './incomeModel';
import { SlidingSwitch } from '../../../ClientEntry/ChatGPTPresetDrawer/ChatGPTIceBreakingDrawerV2/SuggestionTypeSwitchButton';
import YearDetailDialog from './YearDetailDialog';
import './IncomeSimulator.scss';

/**
 * 建團隊收入模擬器 — shown to a candidate in person, in two modes:
 * 🌱 未入行 (personal business against their current job) and 🤝 已入行、想建團隊
 * (overriding from a growing team). The main questions are few; rates live
 * under 進階設定, and the explanations and assumptions are folded away so the
 * first screen stays light.
 *
 * Front end only: the answers are remembered in this browser (localStorage), never
 * sent anywhere. Illustration only, not an income forecast.
 */

type Mode = 'new' | 'team';

/** Looks up an `isim-` message. */
type Translate = (id: string, values?: Record<string, string | number>) => string;
type Series = { key: string; label: string; color: string };
type Row = Record<string, number>;

const COLORS = {
  own: '#1f2a5c',
  firstYear: '#3966f8',
  renewal: '#8fd0c1',
  direct: '#3966f8',
  second: '#8fd0c1',
  reference: '#ea580c',
  mdrt: '#7c3aed'
};

/** A number field; empty or invalid input counts as 0 without blanking what the adviser typed. */
const NumberField = (props: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  prefix?: string;
  suffix?: string;
}) => {
  const { id, label, value, onChange, prefix, suffix } = props;
  const [text, setText] = useState(String(value));
  // Follow resets from outside (還原預設數字) without fighting the adviser's typing.
  const shown = Number(text) === value || (text === '' && value === 0) ? text : String(value);
  return (
    <div className="isim-field">
      <label htmlFor={id}>{label}</label>
      <div className="isim-input">
        {prefix && <span className="isim-affix">{prefix}</span>}
        <NumberFormat
          id={id}
          inputMode="decimal"
          thousandSeparator
          allowNegative={false}
          decimalScale={2}
          isNumericString
          value={shown}
          onValueChange={({ value: raw, floatValue }, { source }) => {
            if (source !== 'event') return;
            setText(raw);
            onChange(floatValue !== undefined && Number.isFinite(floatValue) && floatValue >= 0 ? floatValue : 0);
          }}
        />
        {suffix && <span className="isim-affix">{suffix}</span>}
      </div>
    </div>
  );
};

const Chart = (props: {
  rows: Row[];
  series: Series[];
  showReference: boolean;
  nudge: boolean;
  onPick: (year: number) => void;
  t: Translate;
  chinese: boolean;
}) => {
  const { rows, series, showReference, nudge, onPick, t, chinese } = props;
  const W = 640;
  const H = 300;
  const L = 58;
  const R = 12;
  const T = 28;
  const B = 30;
  const max = niceMax(Math.max(MDRT_COMMISSION, ...rows.map(r => Math.max(r.total, r.reference))));
  const ph = H - T - B;
  const pw = W - L - R;
  const y = (v: number) => T + ph - (v / max) * ph;
  const slot = pw / rows.length;
  const bw = Math.min(64, slot * 0.52);
  return (
    <svg
      className={`isim-chart${nudge ? ' is-nudging' : ''}`}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={t('chart-aria')}
    >
      {[0, 1, 2, 3, 4].map(i => {
        const v = (max / 4) * i;
        return (
          <g key={i}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#e2e8f0" strokeWidth={1} />
            <text x={L - 8} y={y(v) + 4} textAnchor="end">
              {formatWan(v, chinese)}
            </text>
          </g>
        );
      })}
      {rows.map((r, idx) => {
        const cx = L + slot * idx + slot / 2;
        const x0 = cx - bw / 2;
        let acc = 0;
        return (
          <g
            key={idx}
            className="isim-bar"
            role="button"
            tabIndex={0}
            aria-label={t('bar-aria', { year: t('year', { n: idx + 1 }) })}
            onClick={() => onPick(idx)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                // Space would otherwise also scroll the pane behind the pop-up.
                e.preventDefault();
                onPick(idx);
              }
            }}
          >
            <rect
              className="isim-bar-hit"
              style={{ animationDelay: `${idx * 0.25}s` }}
              x={cx - slot / 2}
              y={T}
              width={slot}
              height={H - T - B}
            />
            {series.map(s => {
              const v = r[s.key];
              if (!(v > 0)) return null;
              const top = y(acc + v);
              const bottom = y(acc);
              acc += v;
              return <rect key={s.key} x={x0} y={top} width={bw} height={Math.max(0, bottom - top)} fill={s.color} />;
            })}
            <text className="isim-chart-total" x={cx} y={y(r.total) - 8} textAnchor="middle">
              {formatWan(r.total, chinese)}
            </text>
            {showReference && (
              <line
                x1={x0 - 8}
                x2={x0 + bw + 8}
                y1={y(r.reference)}
                y2={y(r.reference)}
                stroke={COLORS.reference}
                strokeWidth={2}
                strokeDasharray="5 4"
              />
            )}
            <text x={cx} y={H - 8} textAnchor="middle">
              {t('year', { n: idx + 1 })}
            </text>
          </g>
        );
      })}
      <line x1={L} x2={W - R} y1={y(MDRT_COMMISSION)} y2={y(MDRT_COMMISSION)} stroke={COLORS.mdrt} strokeWidth={1.5} />
    </svg>
  );
};

const STORAGE_KEY = 'portfoplus:income-simulator:v1';

/** Keep only the saved fields that still exist and are the right type, over the defaults. */
const mergeSaved = <T extends Record<string, number | boolean>>(defaults: T, saved: unknown): T => {
  const out = { ...defaults };
  if (saved && typeof saved === 'object') {
    (Object.keys(defaults) as Array<keyof T>).forEach(key => {
      const value = (saved as Record<string, unknown>)[key as string];
      if (typeof value === typeof defaults[key] && (typeof value !== 'number' || Number.isFinite(value))) {
        out[key] = value as T[keyof T];
      }
    });
  }
  return out;
};

/** Storage can be blocked or empty (private window, cleared data); the simulator must work without it. */
const loadSaved = () => {
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null') || {};
    return {
      mode: (raw.mode === 'team' ? 'team' : 'new') as Mode,
      newcomer: mergeSaved(NEWCOMER_DEFAULTS, raw.newcomer),
      team: mergeSaved(TEAM_DEFAULTS, raw.team)
    };
  } catch (e) {
    return { mode: 'new' as Mode, newcomer: NEWCOMER_DEFAULTS, team: TEAM_DEFAULTS };
  }
};

const IncomeSimulator = ({ intl }: { intl: InjectedIntl }) => {
  const t: Translate = (id, values) => intl.formatMessage({ id: `isim-${id}` }, values);
  const chinese = /^zh/.test(intl.locale);
  const big = (value: number) => formatWan(value, chinese);
  const people = (value: number) => t('people', { n: formatPeople(value) });
  const modeOptions = [
    { value: 'new', label: t('mode-new'), testId: 'income-sim-mode-new' },
    { value: 'team', label: t('mode-team'), testId: 'income-sim-mode-team' }
  ];
  const yearLabels = Array.from({ length: SIM_YEARS }, (_, i) => t('year', { n: i + 1 }));
  const [saved] = useState(loadSaved);
  const [mode, setMode] = useState<Mode>(saved.mode);
  const [newcomer, setNewcomer] = useState<NewcomerInputs>(saved.newcomer);
  const [team, setTeam] = useState<TeamInputs>(saved.team);
  const [year, setYear] = useState<number | null>(null);
  // Until the first bar is tapped, the chart pulses gently to show that the bars open.
  const [tapped, setTapped] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, newcomer, team }));
    } catch (e) {
      // Not saved; the numbers still work for this visit.
    }
  }, [mode, newcomer, team]);

  const setN = (key: keyof NewcomerInputs) => (value: number) => setNewcomer(prev => ({ ...prev, [key]: value }));
  const setT = (key: Exclude<keyof TeamInputs, 'secondGeneration'>) => (value: number) =>
    setTeam(prev => ({ ...prev, [key]: value }));

  const view = useMemo(() => {
    if (mode === 'new') {
      const years = simulateNewcomer(newcomer);
      const rows: Row[] = years.map(yr => ({
        firstYear: yr.firstYear,
        renewal: yr.renewal,
        total: yr.total,
        reference: yr.salary
      }));
      const first = years[0];
      const last = years[SIM_YEARS - 1];
      const times = last.salary > 0 ? Math.round((last.total / last.salary) * 10) / 10 : 0;
      return {
        rows,
        series: [
          { key: 'firstYear', label: t('series-first'), color: COLORS.firstYear },
          { key: 'renewal', label: t('series-renewal'), color: COLORS.renewal }
        ],
        referenceLabel: t('ref-salary'),
        tiles: [
          {
            hero: true,
            k: t('tile-y1'),
            v: formatMoney(first.total / 12),
            s: t('salary-now', { amount: formatMoney(newcomer.salary) })
          },
          { k: t('tile-y5'), v: formatMoney(last.total / 12), s: t('times-job', { times }) },
          {
            k: t('tile-total'),
            v: big(years.reduce((a, yr) => a + yr.total, 0)),
            s: t('job-5y', { amount: big(years.reduce((a, yr) => a + yr.salary, 0)) })
          }
        ],
        summaryRows: [
          { label: t('row-industry'), kind: 'sum', cell: (r: Row) => formatMoney(r.total) },
          { label: t('ref-salary'), kind: 'reference', cell: (r: Row) => formatMoney(r.reference) },
          { label: t('row-more-job'), kind: 'diff', cell: (r: Row) => differenceOf(r.total, r.reference) }
        ]
      };
    }
    const years = simulateTeam(team);
    const rows: Row[] = years.map(yr => ({
      own: yr.own,
      direct: yr.direct,
      second: yr.second,
      total: yr.total,
      reference: yr.own,
      directMembers: yr.directMembers,
      secondMembers: yr.secondMembers
    }));
    const last = years[SIM_YEARS - 1];
    const perMember = (team.memberMonthly * team.directRate) / 100;
    const series: Series[] = [
      { key: 'own', label: t('series-own'), color: COLORS.own },
      { key: 'direct', label: t('direct-or'), color: COLORS.direct }
    ];
    if (team.secondGeneration) series.push({ key: 'second', label: t('second-or'), color: COLORS.second });
    const summaryRows: Array<{
      label: string;
      kind: string;
      cell: (r: Row) => string | { amount: string; percent: string };
    }> = [
      { label: t('row-team-year'), kind: 'sum', cell: r => formatMoney(r.total) },
      { label: t('row-more-solo'), kind: 'diff', cell: r => differenceOf(r.total, r.reference) },
      { label: t('row-direct'), kind: 'plain', cell: r => people(r.directMembers) }
    ];
    if (team.secondGeneration) {
      summaryRows.push({ label: t('row-second'), kind: 'plain', cell: r => people(r.secondMembers) });
    }
    return {
      rows,
      series,
      referenceLabel: '',
      tiles: [
        {
          hero: true,
          k: t('tile-team-monthly'),
          v: formatMoney((last.direct + last.second) / 12)
        },
        {
          k: t('tile-team-total'),
          v: formatMoney(last.total / 12),
          s: t('solo-about', { amount: formatMoney(last.own / 12) })
        },
        {
          k: t('tile-team-size'),
          v: people(last.directMembers + last.secondMembers),
          s: t('per-member', { amount: formatMoney(perMember) })
        }
      ],
      summaryRows
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, newcomer, team, intl.locale]);

  const reset = () => {
    setNewcomer(NEWCOMER_DEFAULTS);
    setTeam(TEAM_DEFAULTS);
  };

  return (
    <div className="income-simulator" data-testid="income-simulator">
      <SlidingSwitch current={mode} options={modeOptions} onAction={(next: string) => setMode(next as Mode)} />

      <div className="isim-layout">
        <form className="isim-panel" onSubmit={e => e.preventDefault()} autoComplete="off">
          {mode === 'new' ? (
            <>
              <p className="isim-legend">{t('legend-business')}</p>
              <NumberField
                id="isim-cases"
                label={t('f-cases')}
                value={newcomer.cases}
                onChange={setN('cases')}
                suffix={t('u-case')}
              />
              <NumberField
                id="isim-premium"
                label={t('f-premium')}
                value={newcomer.monthlyPremium}
                onChange={setN('monthlyPremium')}
                prefix="$"
              />
              <NumberField
                id="isim-salary"
                label={t('f-salary')}
                value={newcomer.salary}
                onChange={setN('salary')}
                prefix="$"
              />
              <details className="isim-details">
                <summary>{t('adv-new')}</summary>
                <NumberField
                  id="isim-fyc"
                  label={t('f-fyc')}
                  value={newcomer.fycRate}
                  onChange={setN('fycRate')}
                  suffix="%"
                />
                <NumberField
                  id="isim-renewal"
                  label={t('f-renewal')}
                  value={newcomer.renewalRate}
                  onChange={setN('renewalRate')}
                  suffix={t('u-percent-year')}
                />
                <NumberField
                  id="isim-growth"
                  label={t('f-growth')}
                  value={newcomer.growth}
                  onChange={setN('growth')}
                  suffix="%"
                />
                <NumberField
                  id="isim-raise"
                  label={t('f-raise')}
                  value={newcomer.raise}
                  onChange={setN('raise')}
                  suffix="%"
                />
              </details>
            </>
          ) : (
            <>
              <p className="isim-legend">{t('legend-self')}</p>
              <NumberField
                id="isim-own"
                label={t('f-own')}
                value={team.ownMonthly}
                onChange={setT('ownMonthly')}
                prefix="$"
              />
              <p className="isim-legend">{t('legend-team')}</p>
              <NumberField
                id="isim-add"
                label={t('f-add')}
                value={team.addPerYear}
                onChange={setT('addPerYear')}
                suffix={t('u-person')}
              />
              <NumberField
                id="isim-member"
                label={t('f-member')}
                value={team.memberMonthly}
                onChange={setT('memberMonthly')}
                prefix="$"
              />
              <NumberField
                id="isim-direct"
                label={t('direct-or')}
                value={team.directRate}
                onChange={setT('directRate')}
                suffix="%"
              />
              <label className="isim-toggle">
                <input
                  type="checkbox"
                  data-testid="income-sim-second"
                  checked={team.secondGeneration}
                  onChange={e => {
                    const checked = e.target.checked;
                    setTeam(prev => ({ ...prev, secondGeneration: checked }));
                  }}
                />
                {t('toggle-second')}
              </label>
              {team.secondGeneration && (
                <NumberField
                  id="isim-second"
                  label={t('second-or')}
                  value={team.secondRate}
                  onChange={setT('secondRate')}
                  suffix="%"
                />
              )}
              <details className="isim-details">
                <summary>{t('adv-team')}</summary>
                {team.secondGeneration && (
                  <NumberField
                    id="isim-second-per-member"
                    label={t('f-second-per')}
                    value={team.secondPerMember}
                    onChange={setT('secondPerMember')}
                    suffix={t('u-person')}
                  />
                )}
                <NumberField
                  id="isim-own-growth"
                  label={t('f-own-growth')}
                  value={team.growth}
                  onChange={setT('growth')}
                  suffix="%"
                />
              </details>
            </>
          )}
          <button type="button" className="isim-reset" onClick={reset}>
            {t('reset')}
          </button>
        </form>

        <div className="isim-results">
          <div className="isim-tiles">
            {view.tiles.map(tile => (
              <div key={tile.k} className={`isim-tile${'hero' in tile && tile.hero ? ' is-hero' : ''}`}>
                <span className="isim-tile-k">{tile.k}</span>
                <span className="isim-tile-v" data-testid="income-sim-tile">
                  {tile.v}
                </span>
                {'s' in tile && <span className="isim-tile-s">{tile.s}</span>}
              </div>
            ))}
          </div>

          <section className="isim-panel">
            <div>
              <h3>{t('chart-title')}</h3>
              <p className="isim-tap-hint" data-testid="income-sim-tap-hint">
                <span aria-hidden="true">👆</span> {t('tap-hint')}
              </p>
            </div>
            <div className="isim-legend-row">
              {view.series.map(s => (
                <span key={s.key}>
                  <i style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
              {view.referenceLabel && (
                <span>
                  <i className="is-dashed" />
                  {view.referenceLabel}
                </span>
              )}
              <span>
                <i className="is-mdrt" />
                MDRT {formatMoney(MDRT_COMMISSION)}
              </span>
            </div>
            <div className="isim-chart-wrap">
              <Chart
                rows={view.rows}
                series={view.series}
                showReference={mode === 'new'}
                nudge={!tapped}
                t={t}
                chinese={chinese}
                onPick={y => {
                  setTapped(true);
                  setYear(y);
                }}
              />
            </div>
          </section>

          <details className="isim-panel isim-breakdown">
            <summary data-expand={t('expand')} data-collapse={t('collapse')}>
              <h3>{t('breakdown')}</h3>
            </summary>
            <div className="isim-table-wrap">
              <table className="isim-table" data-testid="income-sim-table">
                <thead>
                  <tr>
                    <th />
                    {yearLabels.map(label => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {view.series.map(s => (
                    <tr key={s.key}>
                      <td>
                        <span className="isim-dot" style={{ background: s.color }} />
                        {s.label}
                      </td>
                      {view.rows.map((r, i) => (
                        <td key={i}>{formatMoney(r[s.key])}</td>
                      ))}
                    </tr>
                  ))}
                  {view.summaryRows.map(row => (
                    <tr key={row.label} className={`is-${row.kind}`}>
                      <td>{row.label}</td>
                      {view.rows.map((r, i) => {
                        const cell = row.cell(r);
                        return (
                          <td key={i}>
                            {typeof cell === 'string' ? (
                              cell
                            ) : (
                              <>
                                {cell.amount}
                                <small>{cell.percent}</small>
                              </>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <details className="isim-details isim-fine">
            <summary>{t('assumptions')}</summary>
            <ul>
              {mode === 'new' ? (
                <>
                  <li>{t('as-new-1')}</li>
                  <li>{t('as-new-2')}</li>
                  <li>{t('as-new-3')}</li>
                </>
              ) : (
                <>
                  <li>{t('as-team-1')}</li>
                  <li>{t('as-team-2')}</li>
                  {team.secondGeneration && <li>{t('as-team-3')}</li>}
                  <li>{t('as-team-4')}</li>
                </>
              )}
            </ul>
            <p>{t('disclaimer')}</p>
          </details>
          <p className="isim-oneline">{t('oneline')}</p>
        </div>
      </div>
      <YearDetailDialog
        year={year}
        mode={mode}
        rows={view.rows}
        series={view.series}
        team={team}
        onYear={setYear}
        t={t}
      />
    </div>
  );
};

export default injectIntl(IncomeSimulator);
